import React from 'react';
import Icon from '../../../components/AppIcon';

const ProfileHeader = ({ userProfile, user }) => {
  const getInitials = () => {
    const first = userProfile?.first_name || '';
    const last = userProfile?.last_name || '';
    if (first || last) return `${first?.charAt(0)}${last?.charAt(0)}`?.toUpperCase();
    const fullName = userProfile?.full_name || user?.email || '';
    return fullName?.split(' ')?.map(n => n?.charAt(0))?.join('')?.toUpperCase()?.slice(0, 2) || 'U';
  };

  const getDisplayName = () => {
    if (userProfile?.first_name || userProfile?.last_name) {
      return `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`?.trim();
    }
    return userProfile?.full_name || user?.email || 'User';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role) => {
    const map = {
      administrator: 'bg-red-100 text-red-700 border-red-200',
      user: 'bg-blue-100 text-blue-700 border-blue-200',
      view_only: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return map?.[role] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getRoleLabel = (role) => {
    const map = { administrator: 'Administrator', user: 'User', view_only: 'View Only' };
    return map?.[role] || role || 'User';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
          {userProfile?.avatar_url ? (
            <img src={userProfile?.avatar_url} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <span className="text-xl font-bold text-accent">{getInitials()}</span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-foreground truncate">{getDisplayName()}</h2>
          {userProfile?.nickname && (
            <p className="text-sm text-muted-foreground">@{userProfile?.nickname}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(userProfile?.role)}`}>
              <Icon name="Shield" size={11} />
              {getRoleLabel(userProfile?.role)}
            </span>
            {userProfile?.is_pm && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                <Icon name="Briefcase" size={11} />
                Project Manager
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Meta info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Icon name="Mail" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="text-sm font-medium text-foreground truncate">{user?.email || 'N/A'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Last Login</p>
            <p className="text-sm font-medium text-foreground">{formatDate(userProfile?.last_sign_in_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Calendar" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Member Since</p>
            <p className="text-sm font-medium text-foreground">{formatDate(userProfile?.created_at)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
